import { useCallback, memo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from 'embla-carousel-autoplay';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, MapPin, Award, Star } from "lucide-react";
import certifiedBadge from "@/assets/ekselencia-certified-badge.png";
import RatingBadge from "@/components/RatingBadge";
import { getCategoryDefaultImage, isPlaceholderImage } from "@/utils/imageUtils";

interface FeaturedCarouselProps {
  items: any[];
}

// Memoized carousel item to prevent re-renders
const CarouselItem = memo(({ item }: { item: any }) => {
  const isListing = item.type === 'listing';
  const isNutritionist = item.type === 'nutritionist';
  const isPhysiotherapist = item.type === 'physiotherapist';
  const isTrainer = item.type === 'trainer';

  // Get category-specific default image
  const getDefaultImage = () => {
    if (isNutritionist) return getCategoryDefaultImage('nutritionist');
    if (isPhysiotherapist) return getCategoryDefaultImage('physiotherapist');
    if (isTrainer) return getCategoryDefaultImage('trainer');
    if (isListing && item.business_type) return getCategoryDefaultImage(item.business_type);
    return getCategoryDefaultImage('gym'); // Generic gym default for listings
  };

  const getImage = () => {
    if (isListing) {
      const img = item.thumbnail_url || 
             (item.images && item.images.length > 0 ? item.images[0] : null) || 
             item.image;
      return (img && !isPlaceholderImage(img)) ? img : getDefaultImage();
    }
    const profileImg = item.profile_image;
    return (profileImg && !isPlaceholderImage(profileImg)) ? profileImg : getDefaultImage();
  };

  const getTitle = () => {
    if (isListing) return item.name;
    return item.full_name;
  };

  const getDescription = () => {
    if (isListing) return item.short_description || item.description;
    if (Array.isArray(item.specialty)) {
      return item.specialty.join(", ");
    }
    return item.specialty || "Profesional certificado";
  };

  const getUrl = () => {
    // Listings always go to listing/premium pages
    if (isListing || item.source === 'listing') {
      const url = item.slug || item.id;
      return item.is_premium ? `/premium/${url}` : `/listing/${url}`;
    }

    // Professional profiles go to their dedicated detail routes
    const slug = item.slug || item.id;
    if (isNutritionist) return `/nutritionist/${slug}`;
    if (isPhysiotherapist) return `/physiotherapist/${slug}`;
    if (isTrainer) return `/trainer/${slug}`;

    // Fallback
    return `/listing/${slug}`;
  };

  const getBadgeText = () => {
    if (isNutritionist) return "Nutricionista";
    if (isPhysiotherapist) return "Fisioterapeuta";
    return item.is_premium ? "Premium" : "Gimnasio";
  };

  const getBadgeColor = () => {
    if (isNutritionist) return "bg-green-500";
    if (isPhysiotherapist) return "bg-blue-500";
    return item.is_premium ? "bg-gradient-primary" : "bg-secondary";
  };

  return (
    <div className="embla__slide flex-[0_0_100%] min-w-0 sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] pl-4">
      <Card 
        className="h-[580px] flex flex-col hover:shadow-2xl transition-shadow duration-300 cursor-pointer group border-2 hover:border-primary/50 overflow-hidden will-change-auto"
        onClick={() => window.location.href = getUrl()}
      >
        <div className="relative h-56 overflow-hidden flex-shrink-0">
          <img
            src={getImage()}
            alt={getTitle()}
            loading="lazy"
            decoding="async"
            width="400"
            height="224"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.currentTarget.src = getDefaultImage(); }}
          />
          <div className="absolute top-3 right-3 flex flex-col gap-2 items-end">
            <Badge className={`${getBadgeColor()} text-white text-sm px-3 py-1.5`}>
              {getBadgeText()}
            </Badge>
            {isListing && item.is_premium && (
              <img 
                src={certifiedBadge} 
                alt="Centro Certificado"
                className="w-20 h-20 object-contain drop-shadow-xl"
                width="80"
                height="80"
                loading="lazy"
                decoding="async"
                title="Centro Certificado por Ekselencia.com"
              />
            )}
          </div>
        </div>

        <CardHeader className="flex-shrink-0">
          <CardTitle className="line-clamp-1">{getTitle()}</CardTitle>
          <CardDescription className="line-clamp-2">
            {getDescription()}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex-grow flex flex-col justify-between">
          <div className="flex flex-col gap-3 mb-4">
            {isListing && (
              <>
                <RatingBadge rating={item.rating} />
                <div className="text-sm text-muted-foreground">
                  ({item.review_count || 0} reseñas)
                </div>
              </>
            )}
            
            {!isListing && item.years_experience && (
              <div className="flex items-center gap-1 text-sm">
                <Award className="w-4 h-4 text-primary" />
                <span>{item.years_experience} años de experiencia</span>
              </div>
            )}

            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4" />
              <span>
                {isListing 
                  ? (item.city || "España")
                  : (item.location_city ? `${item.location_city}, ${item.location_country || ''}` : "Internacional")
                }
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2 mt-auto">
            <Button 
              className="w-full bg-gradient-primary"
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = getUrl();
              }}
            >
              Ver Perfil
            </Button>
            <Button 
              variant="outline"
              className="w-full border-2 border-primary/50 hover:bg-primary/10 font-semibold"
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = '/premium-upgrade';
              }}
            >
              <Star className="w-4 h-4 mr-2" />
              Quiero Aparecer Aquí
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

CarouselItem.displayName = "CarouselItem";

export const FeaturedCarousel = memo(({ items }: FeaturedCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: 'start',
    skipSnaps: false,
    dragFree: true, // Enable free dragging for smoother mobile experience
    containScroll: 'trimSnaps',
  }, [Autoplay({ delay: 5000, stopOnInteraction: true })]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <div className="relative px-4 md:px-12">
      <div className="overflow-hidden touch-pan-y" ref={emblaRef}>
        <div className="flex -ml-4 backface-hidden">
          {items.map((item) => (
            <CarouselItem key={`${item.type}-${item.id}`} item={item} />
          ))}
        </div>
      </div>

      {/* Navigation Buttons - Hidden on mobile for cleaner touch experience */}
      <Button
        variant="outline"
        size="icon"
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 rounded-full bg-background/90 hover:bg-background shadow-xl hidden md:flex"
        onClick={scrollPrev}
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 rounded-full bg-background/90 hover:bg-background shadow-xl hidden md:flex"
        onClick={scrollNext}
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6" />
      </Button>
    </div>
  );
});

FeaturedCarousel.displayName = "FeaturedCarousel";
